import { EnumInvitationStatus } from '@calm/evexia-analytics';
import {
    CircleInvitation,
    CircleValidationFieldWithValue,
    CustomAxiosError,
} from '@calm/evexia-web-sdk';
import { identifyApiErrorCode } from '../axios';

/**
 * Describes the status of an invitation
 *
 * Do not use the following statuses:
 * - Deleted
 */
export enum InvitationStatus {
    Pending = 'pending',
    Claimed = 'claimed',
    Expired = 'expired',
    Deleted = 'deleted',
}

export const InviteCodeFieldName = 'invite_code';

export type InvitationErrorType =
    | 'expired'
    | 'claimed'
    | 'country'
    | 'country_mismatch';

export const InvitationErrorMessage: Record<InvitationErrorType, string> = {
    expired: 'invitation_expired',
    claimed: 'invitation_claimed',
    country: 'invitation_country',
    country_mismatch: 'sender_recipient_country_mismatch',
};

export const InvitationPartnerErrorMessage = {
    country: 'no_dependent_group_for_country_code',
};

export function getInviteValidationFields(
    inviteCode: string
): CircleValidationFieldWithValue[] {
    return [
        {
            name: InviteCodeFieldName,
            value: inviteCode,
        },
    ];
}

/**
 * An axios-style error for invitation failures. It is a real `Error` (so it can
 * be thrown) while still carrying the `response` payload that the registration
 * error-handling chain (`isInvitationError` / `identifyInvitationErrorType`)
 * reads.
 */
export class InvitationError extends Error {
    response: CustomAxiosError['response'];
    constructor(type: InvitationErrorType) {
        super(InvitationErrorMessage[type]);
        this.name = 'InvitationError';
        this.response = {
            status: 400,
            data: {
                message: InvitationErrorMessage[type],
            },
        } as CustomAxiosError['response'];
    }
}

export function createInvitationError(
    type: InvitationErrorType
): InvitationError {
    return new InvitationError(type);
}

export function isInvitationError(e: CustomAxiosError): boolean {
    const errorType = identifyInvitationErrorType(e);
    return Boolean(errorType);
}

export function identifyInvitationErrorType(
    e: CustomAxiosError | null
): InvitationErrorType | undefined {
    if (!e) {
        return;
    }

    if (e?.response?.status && e?.response?.status >= 400) {
        if (!!e?.response?.data && typeof e.response.data === 'object') {
            const errorCode = e ? identifyApiErrorCode(e) : null;
            // Handle either errorCode or message having the error
            if (errorCode) {
                switch (errorCode) {
                    case InvitationErrorMessage.claimed:
                        return 'claimed';
                    case InvitationErrorMessage.country:
                        return 'country';
                    case InvitationPartnerErrorMessage.country:
                        return 'country';
                    case InvitationErrorMessage.country_mismatch:
                        return 'country';
                    case InvitationErrorMessage.expired:
                        return 'expired';
                }
            }
            if ('message' in e.response.data) {
                switch (e?.response?.data?.message) {
                    case InvitationErrorMessage.claimed:
                        return 'claimed';
                    case InvitationErrorMessage.country:
                        return 'country';
                    case InvitationPartnerErrorMessage.country:
                        return 'country';
                    case InvitationErrorMessage.country_mismatch:
                        return 'country';
                    case InvitationErrorMessage.expired:
                        return 'expired';
                }
            }
        }
    }
}

/*
 * Pending: invitation.claimedAt == null && invitation not expired
 * Claimed: invitation.claimedAt != null
 * Expired: invitation.expiresAt before now in UTC
 * Deleted: invitation.removedAt != null
 */
export function getInvitationStatus(
    invitation: CircleInvitation
): InvitationStatus {
    if (invitation?.claimedAt) {
        return InvitationStatus.Claimed;
    }
    if (invitation?.removedAt) {
        return InvitationStatus.Deleted;
    }
    if (new Date(invitation.expiresAt).getTime() <= Date.now()) {
        return InvitationStatus.Expired;
    }
    return InvitationStatus.Pending;
}

export const mapInvitationStatusAnalytics = (
    status?: string
): EnumInvitationStatus => {
    if (status === 'pending') return EnumInvitationStatus.Pending;
    if (status === 'claimed') return EnumInvitationStatus.Claimed;
    if (status === 'expired') return EnumInvitationStatus.Expired;
    return EnumInvitationStatus.Unclaimed;
};
